import { Component, Injector } from '@angular/core';
import { Validators } from '@angular/forms';
import { RxFormBuilder, RxFormGroup } from '@rxweb/reactive-form-validators';
import { Client } from '../_types/client';
import { ClientsService } from '../_services/clients.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { TopbarService } from 'src/app/core/topbar/topbar.service';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FormValidatorService } from 'src/app/core/services/form-validator.service';
import { CountriesService } from '../../common/_services/countries.service';
import { firstValueFrom } from 'rxjs';
import { BanksService } from '../../common/_services/banks.service';
import { ClientBankAccount } from '../_types/client-bank-account';
import { ClientBankAccountsService } from '../_services/clients-bank-accounts.service';
import { ClientPolicy } from '../_types/client-policy';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';
import { Filter, FilterOperation } from 'src/app/core/jsonapi/filter';
import { ClientPolicyComponent } from '../client-policy/client-policy.component';
import { ClientPoliciesService } from '../_services/clients-policies.service';
import { CountriesRegionsService } from '../../common/_services/countries-regions.service';
import { ThirdPartySource } from '../../third-parties-sources/_types/third-party-source';
import { ThirdPartiesSourcesService } from '../../third-parties-sources/_services/third-party-source.service';
import { Address } from '../../common/_types/address';
import { AddressesService } from '../../common/_services/addresses.service';
import { Case } from '../../cases/_types/case';
import { CasesService } from '../../cases/_services/cases.service';
import { DateUtilsService } from 'src/app/core/services/date-utils.service';
import { LoginService } from '../../login/login.service';
import { ClientSubtypesService } from '../_services/clients-subtypes.service';

@Component({
    selector: 'app-clients-detail',
    templateUrl: './clients-detail.component.html',
    styleUrls: ['./clients-detail.component.scss'],
    standalone: false
})
export class ClientsDetailComponent {
  public clientFormGroup!: RxFormGroup;

  public client: Client = new Client();
  public banks: any = [];

  public clientPolicies: ClientPolicy[] = [];
  public thirdPartySources: ThirdPartySource[] = [];
  public loadingClientPolicies: boolean = false;
  public totalClientPoliciesRecords: number = 0;

  public allowWrite: boolean = false;
  public allowDelete: boolean = false;

  public countries: any = [];
  private dynamicDialogConfig?: DynamicDialogConfig;
  public countriesRegions: any = [];

  public totalRecords:number = 0;
  public cases:Case[] = [];
  public loading:boolean = false;
  private lastCasesGridEvent:any;

  public clientTypes:any[] = [
    { name: 'Particular', value: 'Person' },
    { name: 'Empresa', value: 'Company' }
  ]
  public clientSubtypes: any[]= [];
  
  constructor(
    private injector: Injector,
    private clientsService: ClientsService,
    private addressesService: AddressesService,
    private clientBankAccountsService: ClientBankAccountsService,
    private clientPoliciesService: ClientPoliciesService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private route: ActivatedRoute,
    private router: Router,
    private topbarService: TopbarService,
    private formBuilder: RxFormBuilder,
    private formValidationService: FormValidatorService,
    private countriesService: CountriesService,
    private banksService: BanksService,
    private dialogService: DialogService,
    private countriesRegionsService: CountriesRegionsService,
    private thirdPartiesSourcesService: ThirdPartiesSourcesService,
    private casesService:CasesService,
    public dateUtilsService:DateUtilsService,
    private loginService: LoginService,
    private clientSubtypeService: ClientSubtypesService
  ) {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.clientFormGroup = this.formBuilder.formGroup<Client>(this.client) as RxFormGroup;
  }

  async ngOnInit() {
    
    this.loadCountries();
    this.loadCountriesRegions();
    this.loadBanks();
    this.loadThirdPartySources();
    this.loadClientSubtypes();

    let id = this.route.snapshot.params['id'];

    //intentamos obtener
    try {
      this.dynamicDialogConfig = this.injector.get(DynamicDialogConfig);
      if (this.dynamicDialogConfig.data.id != null) id = this.dynamicDialogConfig.data.id;
    } catch (e) {
      //nothign to do
    }
    if (id != null && id != '0') {
      await this.loadClient(id);
    }

    if (this.client.address == null) this.client.address = new Address();
    if (this.client.bankAccount == null) this.client.bankAccount = new ClientBankAccount();

    this.allowWrite = this.loginService.hasPermission("CLIENTS.WRITE");
    this.allowDelete = this.loginService.hasPermission("CLIENTS.DELETE");
    if(!this.dynamicDialogConfig){
      this.topbarService.setTitle("Detalles de cliente");
      this.topbarService.addBreadcrumb("Clientes", "/clients");
    }
  }

  async loadClient(id: string) {
    this.client = await firstValueFrom(this.clientsService.get(id, { include: ['bankAccount', 'address'] }));
    this.clientFormGroup.patchValue(this.client);
    this.loadClientPolicies();
    this.loadCases(this.lastCasesGridEvent);
  }

  async loadCountries() {
    let countryId = null;
    if (this.clientFormGroup.value.address?.countryRegionId != null) {
      let country = this.countriesRegions.find((m: any) => m.id == this.client.address?.countryRegionId);
      if (country != null) countryId = country.countryId;
    }
    let params: IParamsCollection = {
      sort: ['name'],
    };
    if (countryId != null) {
      let filters = {
        id: new Filter(FilterOperation.equals, 'id', "", countryId)
      };
      params.filter = FilterBuilder.fromObject(filters).build();
    }
    let response = await firstValueFrom(this.countriesService.all(params));
    this.countries = response.data;
  }

  
  async loadClientSubtypes() {
    let countryId = null;   
    let params: IParamsCollection = {
      sort: ['name'],
    };
    
    let response = await firstValueFrom(this.clientSubtypeService.all(params));
    this.clientSubtypes = response.data;
  }


  async loadCases($event: any) {
    this.lastCasesGridEvent = $event;

    if(this.client.id==null || this.client.id=="") return;

    let filters = {
      clientId: new Filter(FilterOperation.equals, 'clientId', "", this.client.id)
    };
    let params: IParamsCollection = {
      sort: ['-caseDate'],
      include: ['caseType', 'caseState', 'client', 'company', 'contract', 'processor', 'address'],
      page: { number: $event.first + 1, size: $event.rows },
      filter: FilterBuilder.fromObject(filters).build()
    };
    if (typeof $event.sortField != "undefined") params.sort = [($event.sortOrder > 0 ? "-" : "") + $event.sortField];
    let response = await firstValueFrom(this.casesService.all(params));
    this.cases = response.data;
  }

  async loadBanks() {
    let params: IParamsCollection = {
      page: { size: 1000, number: 1 },
      sort: ['name']
    };
    let response = await firstValueFrom(this.banksService.all(params));
    this.banks = response.data;
  }

  async loadClientPolicies() {
    this.loadingClientPolicies = true;
    let params: IParamsCollection = {
      include: ['insuranceCompany'],
    }
    let filters: any = {
      clientId: new Filter(FilterOperation.equals, "clientId"),
    };

    filters.clientId.value = this.client.id;
    params.filter = FilterBuilder.fromObject(filters).build();

    let response = await firstValueFrom(this.clientPoliciesService.all(params));
    this.clientPolicies = response.data;
    this.totalClientPoliciesRecords = response.meta['total'];
    this.loadingClientPolicies = false;
  }

  async loadCountriesRegions() {
    let params: IParamsCollection = {
      sort: ['name']
    };
    let response = await firstValueFrom(this.countriesRegionsService.all(params));
    this.countriesRegions = response.data;
  }

  async loadThirdPartySources() {
    let response = await firstValueFrom(this.thirdPartiesSourcesService.all());
    this.thirdPartySources = response.data;
  }

  async save() {
    let valid = this.formValidationService.validateForm(this.clientFormGroup);
    if (valid) {
      this.client = Object.assign(this.client, this.clientFormGroup.value);
      if(this.client.priorityPhone != null) this.client.priorityPhone = parseInt(this.client.priorityPhone +"");
      if (this.client.address != null && this.client.address.addressLine1 != null) {
        let address: any = await firstValueFrom(this.addressesService.save(this.client.address));
        this.client.addressId = address.id;
      }

      //guardamos cuenta bancaria, si hay datos
      if (this.client.bankAccount != null && this.client.bankAccount.bankId != null) {
        let bankAccount: any = await firstValueFrom(this.clientBankAccountsService.save(this.client.bankAccount));
        this.client.bankAccountId = bankAccount.id;
      }

      let client = await firstValueFrom(this.clientsService.save(this.client));
      this.messageService.add({ closable: true, severity: 'success', summary: "Éxito", detail: "Cliente guardado correctamente." });
      
      //si es nuevo, recargamos cliente, si no, redirigimos a la vista de cliente
      if (client.id != "undefined" && client.id != "") {
              
        if (this.dynamicDialogConfig) {
          let ref = this.injector.get(DynamicDialogRef);
          ref.close(client);
        } else {
          this.router.navigate(['clients/' + client.id]);
          this.loadClient(client.id!);
        }
      } else {
        if (this.dynamicDialogConfig) {
          let ref = this.injector.get(DynamicDialogRef);
          ref.close(client);
        } else {
          
          this.router.navigate(['clients/' + client.id]);
        }
      }

    } else {
      this.messageService.add({ closable: true, severity: 'error', summary: "Error", detail: "Existen errores, por favor revise el formulario de datos." });
    }
  }

  async delete() {
    this.confirmationService.confirm({
      message: "¿Está seguro que desea eliminar el cliente?",
      header: "Eliminar cliente",
      acceptLabel: "Aceptar",
      rejectLabel: "Cancelar",
      acceptButtonStyleClass: "p-button-danger",
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.clientsService.delete(this.client.id).subscribe({
          next: (data: any) => {
            this.cancel();
            this.messageService.add({ closable: true, severity: 'success', detail: "Cliente eliminado correctamente." });
          },
          error: (error: any) => {
            this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
          }
        });
      },
      reject: (error: any) => {
        this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
      }
    });
  }

  cancel() {
    if (this.dynamicDialogConfig) {
      let ref = this.injector.get(DynamicDialogRef);
      ref.close();
    } else {
      this.router.navigate(['clients']);
    }
  }

  async editClientPolicy(clientPolicy: ClientPolicy | null) {
    const ref = this.dialogService.open(ClientPolicyComponent, {
      closable: true,
      modal: true,
      header: 'Ficha de la póliza',
      width: '80%',
      data: {        
        id: clientPolicy?.id ? clientPolicy.id : null,
        clientId: this.client.id
      }
    })

    ref.onClose.subscribe(() => {
      this.loadClientPolicies();
    });
  }


  async deleteClientPolicy(clientPolicyId: string) {
    this.confirmationService.confirm({
      message: "¿Está seguro que desea eliminar la póliza?",
      header: "Eliminar póliza",
      acceptLabel: "Aceptar",
      rejectLabel: "Cancelar",
      acceptButtonStyleClass: "p-button-danger",
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.clientPoliciesService.delete(clientPolicyId).subscribe({
          next: (data: any) => {
            this.loadClientPolicies();
            this.messageService.add({ closable: true, severity: 'success', detail: "Póliza eliminada correctamente." });
          },
          error: (error: any) => {
            this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
          }
        });
      },
      reject: (error: any) => {
        this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
      }
    });
  }

  getRevisionDateClass(date: string | Date): string {
    const today = new Date();
    const revisionDate = new Date(date);
      
    const isToday =
      revisionDate.getFullYear() === today.getFullYear() &&
      revisionDate.getMonth() === today.getMonth() &&
      revisionDate.getDate() === today.getDate();
  
    if (revisionDate < today && !isToday) {
      return 'text-danger';
    } else if (isToday) {
      return 'text-warning';
    } else {
      return '';
    }
  }

}
